/**
 * Browser Compatibility Utilities
 * 
 * This module provides utilities for detecting browser compatibility issues
 * and managing user notifications about potential problems.
 */

const BROWSER_WARNING_STORAGE_KEY = 'bylaw_browser_warning_shown';

/**
 * Detects if the current browser is potentially incompatible with the application
 * @returns {boolean} True if the browser might have compatibility issues
 */
export function isUnsupportedBrowser(): boolean {
  const userAgent = navigator.userAgent;
  
  // Check for Trident (Internet Explorer) or Edge Legacy
  return userAgent.indexOf("Trident") !== -1 || userAgent.indexOf("Edge") !== -1;
}

/**
 * Checks if the browser compatibility warning has already been shown
 * @returns {boolean} True if the warning has been shown before
 */
export function hasShownBrowserWarning(): boolean {
  try {
    return localStorage.getItem(BROWSER_WARNING_STORAGE_KEY) === 'true';
  } catch (error) {
    // If localStorage is not available, assume warning hasn't been shown
    console.warn('localStorage not available for browser warning tracking:', error);
    return false;
  }
}

/**
 * Marks the browser compatibility warning as shown
 */
export function markBrowserWarningAsShown(): void {
  try {
    localStorage.setItem(BROWSER_WARNING_STORAGE_KEY, 'true');
  } catch (error) {
    console.warn('Could not save browser warning state to localStorage:', error);
  }
}

/**
 * Gets the appropriate warning message for unsupported browsers
 * @returns {string} The warning message to display
 */
export function getBrowserWarningMessage(): string {
  return "Votre navigateur pourrait ne pas être entièrement compatible avec cette extension. Pour une expérience optimale, veuillez mettre à jour vers Office 2021 ou Microsoft 365.";
}

/**
 * Determines if a browser compatibility warning should be shown
 * @returns {boolean} True if the warning should be displayed
 */
export function shouldShowBrowserWarning(): boolean {
  return isUnsupportedBrowser() && !hasShownBrowserWarning();
}